import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getRoot, type LexicalEditor } from "lexical";
import { useEffect } from "react";

import { $isProductBlockNode } from "./ProductBlockNode";
import { $isDiscoverHeadingLine } from "./ProductBlockPlugin";
import {
  $isProductContextNode,
  PRODUCT_MIRROR_BODY_CLASS,
  PRODUCT_MIRROR_CLASS,
  PRODUCT_MIRROR_HEADER_CLASS,
  PRODUCT_MIRRORS_CLASS,
  PRODUCT_MIRRORS_ON_CLASS,
  PRODUCT_MIRROR_OPEN_CLASS,
} from "./ProductContextNode";

/** Static copy of an authored line for a read-only mirror row. */
function cloneForMirror(source: HTMLElement): HTMLElement {
  const clone = source.cloneNode(true) as HTMLElement;
  clone.removeAttribute("contenteditable");
  for (const el of clone.querySelectorAll("[contenteditable]")) {
    el.removeAttribute("contenteditable");
  }
  for (const el of clone.querySelectorAll(".lexical-product-delete")) {
    el.remove();
  }
  return clone;
}

/**
 * Shows the sets 2–5 rows under the message's lone discover section, each
 * expanded row holding a static copy of the authored blocks and headline
 * lines. Nothing here touches the editor state.
 */
function refreshMirrors(editor: LexicalEditor): void {
  editor.getEditorState().read(() => {
    // Every context builds the mirror rails, so the scan can't be narrowed to
    // the discover ones: a section switched *off* discover still has its rails
    // showing, and only this pass can put them away again.
    const contexts = $getRoot().getChildren().filter($isProductContextNode);
    const discover = contexts.filter(
      (context) => context.getModel() === "discover",
    );
    const sole = discover.length === 1 ? discover[0] : null;
    for (const context of contexts) {
      const dom = editor.getElementByKey(context.getKey());
      const mirrors = dom?.querySelector(`.${PRODUCT_MIRRORS_CLASS}`);
      if (!(mirrors instanceof HTMLElement)) continue;
      const on = context === sole;
      mirrors.classList.toggle(PRODUCT_MIRRORS_ON_CLASS, on);
      if (!on) continue;
      const sources = context
        .getChildren()
        .filter(
          (child) =>
            $isProductBlockNode(child) || $isDiscoverHeadingLine(child),
        )
        .map((child) => editor.getElementByKey(child.getKey()))
        .filter((el): el is HTMLElement => el instanceof HTMLElement);
      for (const row of mirrors.querySelectorAll(
        `.${PRODUCT_MIRROR_OPEN_CLASS}`,
      )) {
        row
          .querySelector(`.${PRODUCT_MIRROR_BODY_CLASS}`)
          ?.replaceChildren(...sources.map(cloneForMirror));
      }
    }
  });
}

export function ProductMirrorPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const toggle = (event: Event) => {
      if (
        event instanceof KeyboardEvent &&
        event.key !== "Enter" &&
        event.key !== " "
      ) {
        return;
      }
      const target = event.target as HTMLElement | null;
      const header = target?.closest?.(`.${PRODUCT_MIRROR_HEADER_CLASS}`);
      // Pressing on a mirror header must not move the caret into it.
      if (event.type === "mousedown") {
        if (header) event.preventDefault();
        return;
      }
      if (!(header instanceof HTMLElement)) return;
      event.preventDefault();
      event.stopPropagation();
      const row = header.closest(`.${PRODUCT_MIRROR_CLASS}`);
      if (!(row instanceof HTMLElement)) return;
      const open = row.classList.toggle(PRODUCT_MIRROR_OPEN_CLASS);
      header.setAttribute("aria-expanded", String(open));
      const chevron = header.firstChild;
      if (chevron instanceof HTMLElement)
        chevron.textContent = open ? "▾" : "▸";
      if (!open) {
        row.querySelector(`.${PRODUCT_MIRROR_BODY_CLASS}`)?.replaceChildren();
      }
      refreshMirrors(editor);
    };
    return editor.registerRootListener((rootElement, prevRootElement) => {
      for (const type of ["mousedown", "click", "keydown"] as const) {
        prevRootElement?.removeEventListener(type, toggle);
        rootElement?.addEventListener(type, toggle);
      }
    });
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(() => refreshMirrors(editor));
  }, [editor]);

  // Merge-tag chips are React decorators that paint after the update
  // listener has already cloned, so a new chip would mirror as an empty span
  // until the next edit. Re-clone once React has flushed them.
  useEffect(() => {
    return editor.registerDecoratorListener(() => {
      queueMicrotask(() => refreshMirrors(editor));
    });
  }, [editor]);

  return null;
}
